import React, { useState } from 'react';
import { calculateDeterministicCosts } from '../utils/deterministic-pricing';
import '../styles/pricing-form.css';

const PricingForm = ({ onSubmit, onConsultationRequest, onProjectTypeChange }) => {
  const [formData, setFormData] = useState({
    projectType: '',
    skuCount: '',
    documentCount: '',
    warehouseCount: 1,
    integrationType: 'none',
    dataSize: { size: 100, unit: 'GB' },
    specializedUseCase: 'none',
    needsMemory: false,
    needsTools: false,
    needsOrchestration: false,
    hasIoTIntegration: false,
    hasRealtimeRequirement: false,
    hasComplianceRequirements: false,
    isEnterpriseScale: false,
    integrationCount: 1,
    useSelfServiceWizard: true
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for project type changes to reset project-specific fields
    if (name === 'projectType') {
      // Create a clean state with default values for the new project type
      const resetState = {
        ...formData,
        projectType: value,
        // Reset project-specific fields
        skuCount: '',
        documentCount: '',
        warehouseCount: 1,
        specializedUseCase: 'none',
        needsMemory: false,
        needsTools: false,
        needsOrchestration: false
      };
      setFormData(resetState);
      
      // Notify parent component to clear price estimate
      if (typeof onProjectTypeChange === 'function') {
        onProjectTypeChange();
      }
      return;
    }
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'dataSize.size') {
      setFormData({ 
        ...formData, 
        dataSize: { ...formData.dataSize, size: parseInt(value) || 0 } 
      });
    } else if (name === 'dataSize.unit') {
      setFormData({ 
        ...formData, 
        dataSize: { ...formData.dataSize, unit: value } 
      });
    } else if (type === 'number') {
      setFormData({ ...formData, [name]: parseInt(value) || '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Calculate costs using deterministic pricing
    const costs = calculateDeterministicCosts({
      ...formData,
      useDeterministicPricing: true
    });
    
    // Pass the form data and costs to parent component
    onSubmit(formData, costs);
  };

  // Show/hide specialized fields based on project type
  const renderSpecializedFields = () => {
    switch (formData.projectType) {
      case 'inventory':
        return (
          <>
            <div className="form-group">
              <label htmlFor="skuCount">Number of SKUs/Products:</label>
              <input
                type="number"
                id="skuCount"
                name="skuCount"
                value={formData.skuCount}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="warehouseCount">Number of Warehouses:</label>
              <input
                type="number"
                id="warehouseCount"
                name="warehouseCount"
                value={formData.warehouseCount}
                onChange={handleChange}
                min="1"
              />
            </div>
          </>
        );
      
      case 'document_ai':
        return (
          <>
            <div className="form-group">
              <label htmlFor="documentCount">Number of Documents:</label>
              <input
                type="number"
                id="documentCount"
                name="documentCount"
                value={formData.documentCount}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="specializedUseCase">Document Type:</label>
              <select
                id="specializedUseCase"
                name="specializedUseCase"
                value={formData.specializedUseCase}
                onChange={handleChange}
              >
                <option value="none">General Documents</option>
                <option value="legal_nlp">Legal Documents</option>
                <option value="medical_nlp">Medical Documents</option>
                <option value="financial_nlp">Financial Documents</option>
              </select>
            </div>
          </>
        );
      
      case 'genai_agent':
        return (
          <>
            <div className="form-group checkbox-group">
              <label>Agent Capabilities:</label>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="needsMemory"
                  name="needsMemory"
                  checked={formData.needsMemory}
                  onChange={handleChange}
                />
                <label htmlFor="needsMemory">Persistent Memory</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="needsTools"
                  name="needsTools"
                  checked={formData.needsTools}
                  onChange={handleChange}
                />
                <label htmlFor="needsTools">Tool Integration</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="needsOrchestration"
                  name="needsOrchestration"
                  checked={formData.needsOrchestration}
                  onChange={handleChange}
                />
                <label htmlFor="needsOrchestration">Workflow Orchestration</label>
              </div>
            </div>
          </>
        );
      
      case 'computer_vision':
        return (
          <>
            <div className="form-group">
              <label htmlFor="specializedUseCase">Vision Type:</label>
              <select
                id="specializedUseCase"
                name="specializedUseCase"
                value={formData.specializedUseCase}
                onChange={handleChange}
              >
                <option value="none">General Computer Vision</option>
                <option value="medical_imaging">Medical Imaging</option>
                <option value="object_detection">Object Detection</option>
                <option value="facial_recognition">Facial Recognition</option>
              </select>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="pricing-form-container">
      <h2>AI Project Pricing Calculator</h2>
      <p className="form-description">
        Fill out this form to get an accurate price estimate for your AI project.
      </p>
      
      <form onSubmit={handleSubmit} className="pricing-form">
        <div className="form-group">
          <label htmlFor="projectType">Project Type:</label>
          <select
            id="projectType"
            name="projectType"
            value={formData.projectType}
            onChange={handleChange}
            required
          >
            <option value="">Select Project Type</option>
            <option value="inventory">Inventory Management</option>
            <option value="document_ai">Document AI</option>
            <option value="computer_vision">Computer Vision</option>
            <option value="genai_agent">GenAI Agent</option>
            <option value="chatbot">Chatbot</option>
            <option value="analytics">Data Analytics</option>
            <option value="recommendation">Recommendation System</option>
          </select>
        </div>
        
        {formData.projectType && renderSpecializedFields()}
        
        <div className="form-group">
          <label htmlFor="integrationType">Integration Type:</label>
          <select
            id="integrationType"
            name="integrationType"
            value={formData.integrationType}
            onChange={handleChange}
          >
            <option value="none">No Integration</option>
            <option value="CRM">CRM System</option>
            <option value="ERP">ERP System</option>
            <option value="POS">POS System</option>
            <option value="general">Other Systems</option>
          </select>
        </div>
        
        <div className="form-group data-size-group">
          <label>Data Size:</label>
          <div className="data-size-inputs">
            <input
              type="number"
              name="dataSize.size"
              value={formData.dataSize.size}
              onChange={handleChange}
              min="0"
            />
            <select
              name="dataSize.unit"
              value={formData.dataSize.unit}
              onChange={handleChange}
            >
              <option value="MB">MB</option>
              <option value="GB">GB</option>
              <option value="TB">TB</option>
            </select>
          </div>
        </div>
        
        <div className="form-group checkbox-group">
          <label>Advanced Features:</label>
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="hasIoTIntegration"
              name="hasIoTIntegration"
              checked={formData.hasIoTIntegration}
              onChange={handleChange}
            />
            <label htmlFor="hasIoTIntegration">IoT Integration</label>
          </div>
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="hasRealtimeRequirement"
              name="hasRealtimeRequirement"
              checked={formData.hasRealtimeRequirement}
              onChange={handleChange}
            />
            <label htmlFor="hasRealtimeRequirement">Real-time Processing</label>
          </div>
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="hasComplianceRequirements"
              name="hasComplianceRequirements"
              checked={formData.hasComplianceRequirements}
              onChange={handleChange}
            />
            <label htmlFor="hasComplianceRequirements">Compliance Requirements</label>
          </div>
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="isEnterpriseScale"
              name="isEnterpriseScale"
              checked={formData.isEnterpriseScale}
              onChange={handleChange}
            />
            <label htmlFor="isEnterpriseScale">Enterprise Scale</label>
          </div>
        </div>
        
        <div className="form-group checkbox-group">
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="useSelfServiceWizard"
              name="useSelfServiceWizard"
              checked={formData.useSelfServiceWizard}
              onChange={handleChange}
            />
            <label htmlFor="useSelfServiceWizard">Use Self Service Build Wizard (70% discount)</label>
          </div>
        </div>
        
        <div className="form-actions">
          <button type="submit" className="calculate-button">Calculate Price</button>
          <button type="button" className="consultation-button" onClick={onConsultationRequest}>
            Request Free Consultation
          </button>
        </div>
      </form>
    </div>
  );
};

export default PricingForm;